import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {useEffect, useRef} from 'react';
import styled from 'styled-components';
import {
  mapInstanceAtom, mapMobileCenterAtom, mobileAnalysisPointAtom, zoomLevelAtom
} from '@/map/atom';
import {MobileMapAgent} from '@/map/enums';
import {useCreateMap, usePreventMapScrollEvent} from '@/map/hooks';
import {BottomSection} from './BottomSection';
import {MarkerCluster} from './MarkerCluster';

export const MAP_DOM_ID = 'dynamic-maps-container';

interface IProps {
  agent?: MobileMapAgent;
}

export const Map = ({agent = MobileMapAgent.myfc}: IProps) => {
  const {map, createMap} = useCreateMap();
  const [zoom, setZoomLevel] = useAtom(zoomLevelAtom);
  const [center, setCenter] = useAtom(mapMobileCenterAtom(agent));
  const setMapAtom = useSetAtom(mapInstanceAtom);
  const analysisPoint = useAtomValue(mobileAnalysisPointAtom);

  const preventScrollEvent = usePreventMapScrollEvent();
  const idleEvent = useRef<naver.maps.MapEventListener>();

  useEffect(() => {
    const instance = createMap(MAP_DOM_ID, {
      scaleControlOptions: {
        position: naver.maps.Position.RIGHT_TOP,
      },
    });

    if (instance) {
      instance.addListenerOnce('init', () => {
        // setInit(true);
      });

      return () => {
        instance.destroy();
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }

    idleEvent.current = map.addListener('idle', () => {
      setZoomLevel(map.getZoom());
      const coords = map.getCenter();
      setCenter((prev) => {
        if (prev && coords.equals(prev)) {
          return prev;
        }

        map.setOptions({zoomOrigin: coords});
        return coords;
      });
    });

    return () => {
      if (idleEvent.current) {
        map.removeListener(idleEvent.current);
        idleEvent.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (map) {
      map.setOptions('scrollWheel', !preventScrollEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, preventScrollEvent]);

  useEffect(() => {
    if (map && zoom && map.getZoom() !== zoom) {
      map.setZoom(zoom, false);
    }
  }, [map, zoom]);

  useEffect(() => {
    if (map && center && !map.getCenter().equals(center)) {
      map.setCenter(center);
      map.setOptions({
        zoomOrigin: center,
      });
    }
  }, [map, center]);

  useEffect(() => {
    setMapAtom(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return (
    <Root>
      {!!analysisPoint?.center && <MarkerCluster map={map} />}
      <BottomSection map={map} agent={agent} />
      <MapContainer id={MAP_DOM_ID} />
    </Root>
  );
};

const Root = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  overflow: hidden;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  outline: 0;
`;
